import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import userActions from "../../../_actions/userActions";
import unitActions from "../../../_actions/unitActions";
import distOptions from "../../../_constants/distOptions";
import Table from "../../_common/Table";
import AdminUsersDetails from "./AdminUsersDetails";
import { USER } from "../../../_constants/actionConstants";
import "./adminUsers.css";
import AddUserModalV2 from "./AddUserModalV2";

export default function AdminUsersV2(props) {
  const { tabId } = props;

  const dispatch = useDispatch();

  const isInitialRender = useRef(true);

  const isLastPage = useSelector(
    (state) => state.users.openUserListsV2[tabId]?.isLast ?? true
  );
  const userList = useSelector(
    (state) => state.users.openUserListsV2[tabId]?.data || null
  );
  const unitList = useSelector((state) => state.users.openUnitLists[tabId] || null);
  const userRoles = useSelector((state) => state.users.userRoles || null);
  const fetching = useSelector((state) => state.users.fetching || false);

  const [distFilter, setDistFilter] = useState(null);
  const [unitFilter, setUnitFilter] = useState(null);
  const [userFilter, setUserFilter] = useState("");
  const [sortColumn, setSortColumn] = useState({ path: "fullName", order: "asc" });

  const [selectedUser, setSelectedUser] = useState(null);

  const [modalState_addUser, setModalState_addUser] = useState({
    showModal: false,
    data: null,
  });
  const toggleModal_addUser = () =>
    setModalState_addUser({
      ...modalState_addUser,
      showModal: !modalState_addUser.showModal,
    });

  const filters = useMemo(
    () => ({
      district: distFilter?.value,
      unitId: unitFilter?.value,
      searchString: userFilter || "",
    }),
    [distFilter, unitFilter, userFilter]
  );

  useEffect(() => {
    return () => {
      // Clean up on unmount
      dispatch({ type: USER.CLEAR_USER_DETAILS });
      dispatch({ type: USER.CLEAR_USER_LIST_V2, tabId });
      dispatch({ type: USER.CLEAR_MISC_SESSION_DATA, tabId });
    };
  }, []);

  const unitOptions = useMemo(
    () => (unitList || []).map((u) => ({ value: u.unitId, label: u.distUnit })),
    [unitList]
  );

  const columns = useRef([
    {
      path: "fullName",
      label: "Name",
      thClassName: "admin-users-table__th--name",
      content: (user) => <b>{user.fullName}</b>,
    },
    {
      path: "employeeNo",
      label: "S#",
      thClassName: "admin-users-table__th--employeeNo",
      content: (user) => <small>{user.employeeNo}</small>,
    },
    {
      path: "userRoleDescription",
      label: "Title",
      thClassName: "admin-users-table__th--title",
      content: (user) => getUserRoleDescription(user.userRoleId),
    },
    {
      path: "distUnit",
      label: "Unit",
      thClassName: "admin-users-table__th--distUnit",
      content: (user) => user.distUnit || "(N/A)",
    },
  ]);

  const handleSearch = useCallback((filters) => {
    dispatch({
      type: USER.SET_USER_SEARCH_WAITINGFOR,
      waitingFor: filters.searchString,
      tabId,
    });
    dispatch({ type: USER.CLEAR_USER_LIST_V2, tabId });
    dispatch({
      type: USER.UPDATE_LIST_FILTER,
      tabId,
      filters,
    });
    dispatch(userActions.getNextChunk(tabId, filters));
  }, []);

  const debounceSearch = useCallback(
    _.debounce((filters) => handleSearch(filters), 250),
    []
  );
  const throttleSearch = useCallback(
    _.throttle((filters) => handleSearch(filters), 250),
    []
  );

  useEffect(() => {
    if (!isInitialRender.current) {
      if (userFilter.length < 5) {
        debounceSearch.cancel();
        debounceSearch(filters);
      } else if (userFilter.length > 5) {
        throttleSearch(filters);
      }
    } else isInitialRender.current = false;
  }, [userFilter]);

  useEffect(() => {
    dispatch(unitActions.getAll(distFilter?.value, tabId));
    setUnitFilter(null);
  }, [distFilter]);

  useEffect(() => {
    handleSearch(filters);
    if (isInitialRender.current) isInitialRender.current = false;
  }, [distFilter, unitFilter]);

  const getUserRoleDescription = useCallback(
    (userRoleId) => {
      const role = (userRoles || []).find((ur) => ur.userRoleId === userRoleId);
      return role ? role.description : "(N/A)";
    },
    [userRoles]
  );

  const renderTableFilters = () => {
    return (
      <div className="d-flex flex-row flex-wrap w-100 mb-1 admin-filter-group">
        <div className="admin-filter-item admin-filter-item__select--user-dist pr-2">
          <Select
            name="distFilter"
            id="distFilter"
            className="w-100"
            value={distFilter}
            options={distOptions.with59}
            onChange={(selectedDist) => setDistFilter(selectedDist)}
            placeholder="Dist"
            isSearchable={false}
            isClearable={true}
          />
        </div>
        <div className="admin-filter-item admin-filter-item__select--user-unit pr-2">
          <Select
            name="unitFilter"
            id="unitFilter"
            className="w-100"
            value={unitFilter}
            options={unitOptions || []}
            onChange={(selectedUnit) => setUnitFilter(selectedUnit)}
            placeholder="Unit"
            isSearchable={true}
            isClearable={true}
          />
        </div>
        <div className="admin-filter-item admin-filter-item__input--user flex-grow-1">
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text bg-lightgray">
                <FontAwesomeIcon icon="search" className="fa-fw mx-0 text-muted" />
              </span>
            </div>
            <input
              name="userFilter"
              id="userFilter"
              className="form-control"
              value={userFilter}
              onChange={(e) => {
                setUserFilter(e.target.value);
              }}
              placeholder="Name or S#"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <h2 className="mb-1">Users</h2>
      <hr className="mt-0 mb-2" />
      <div className="d-flex flex-row">
        <div className="admin-list pr-1">
          <div className="form-inline mb-2">
            <button
              type="button"
              name="addUser"
              id="addUser"
              className="btn btn-info mr-2"
              onClick={() =>
                setModalState_addUser({ showModal: true, data: { user: null } })
              }
            >
              <FontAwesomeIcon icon="user-plus" className="fa-fw mr-1" />
              New...
            </button>
          </div>
          {renderTableFilters()}
          <div className="admin-list__table--wrapper border">
            <Table
              className="table table-sm table-hover admin-list__table"
              data={userList || []}
              idPropName="userId"
              selectedItemId={selectedUser && selectedUser.userId}
              columns={columns.current}
              sortColumn={sortColumn}
              onSort={(sortColumn) => setSortColumn(sortColumn)}
              disableAllSort={true}
              handleClick_tr={(selectedUser) => setSelectedUser(selectedUser)}
              isPaginated={true}
              currPage={isLastPage ? 1 : 0}
              pageCount={1}
              getNextPage={() => dispatch(userActions.getNextChunk(tabId, filters))}
              fetching={fetching}
            />
          </div>
        </div>
        <div className="admin-details">
          <div className="card">
            <div className="card-header">User Details</div>
            <div className="card-body">
              {selectedUser && (
                <AdminUsersDetails
                  tabId={tabId}
                  userId={selectedUser.userId}
                  getUserRoleDescription={getUserRoleDescription}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {modalState_addUser.showModal && (
        <AddUserModalV2
          tabId={tabId}
          showModal={modalState_addUser.showModal}
          toggleModal={toggleModal_addUser}
          user={null}
          allowRegisterAsOfficeAdmin={true}
        />
      )}
    </React.Fragment>
  );
}
